import { type JanusCtx, type Row, handBack, itemField, refuse, upper } from './context.js';
import { layerListEntryUuid } from './statuses/status-trigger-gate.js';
import { geometryEnvironment } from '../geometry/port-environment.js';
import { checkUuid } from '../pl/contract.js';
import { values } from '../port/k3-rules/access.js';
import { type CreatureGateDeps, ftCreatureTypeGateUuidLists, ftCreatureTypeGateAllows } from '../port/k3-rules/ft_creature_type_gate.js';
import { targetStatusGatesMet } from './statuses/target-status-gate.js';

export const CT_GATE_KEYS = ['ability_works_only_against_creature_type', 'ability_does_not_work_against_creature_type',
  'ability_works_only_against_creature_sub_type', 'ability_works_only_against_creature_lineage', 'ability_works_only_against_creature_ancestry',
  'ability_works_only_against_creature_grouping', 'ability_does_not_work_against_creature_sub_type', 'ability_does_not_work_against_creature_lineage',
  'ability_does_not_work_against_creature_ancestry', 'ability_does_not_work_against_creature_grouping'];
const opposingOrNull = (row: Row | null): Row | null => row && Object.keys(row).length ? row : null;

/** Directed and delayed casts reject an ineligible type before rolling or spending resources. */
export function creatureTypeCastPreflight(ctx: JanusCtx, ability: Row, targets: Row[]): void {
  if (!ctx.read.has(ability, 'ability_works_only_against_creature_type')) return;
  const ids = (row: Row, key: string): string[] => ctx.read.entries(row, key)
    .map(entry => upper(layerListEntryUuid(entry))).filter(checkUuid);
  const allowed = ids(ability, 'ability_works_only_against_creature_type');
  if (!allowed.length || targets.length !== 1) handBack('casting_roll:creature-type-gate-input');
  const targetTypes = ids(targets[0]!, 'creature_type');
  if (!targetTypes.length) handBack('ability_target_gate_failure:classification-not-held');
  if (targetTypes.some(id => allowed.includes(id))) return;
  const name = String(itemField(ability, 'item_name') ?? 'This ability');
  if (/[<&>"']/.test(name)) handBack('casting_roll:creature-type-gate-html-name');
  refuse(`${name} only works against certain creature types — this target does not qualify.`);
}

/** Bridge native page readers to the shared k3 gate; no rule is reimplemented here. */
export function creatureTypeGateAllows(ctx: JanusCtx, source: Row, opposing: Row | null): boolean {
  const unused = (): never => { throw new Error('creature-type-gate:unexpected-status-reader'); };
  const env = geometryEnvironment<CreatureGateDeps>({
    layer_entries: (row, key) => ctx.read.entries(row as Row, key),
    layer_value: (row, key) => ctx.read.value(row as Row, key),
    ft_normalize_layer_entries_to_distinct_uuids: entries => [...new Set(values(entries).map(layerListEntryUuid).filter(checkUuid))],
    layer_list_entry_uuid: layerListEntryUuid,
    array_intersect: (left, right) => values(left).filter(id => values(right).includes(id)),
    v_status_id_of: unused, creature_statuses: unused, has_status_effect: unused,
  });
  return ftCreatureTypeGateAllows(ftCreatureTypeGateUuidLists(source, env), opposingOrNull(opposing), env);
}

/** Own ADV/DIS sources use the opposing creature; passive/feat status gates fail closed without one. */
export function rollSourceGateAllows(ctx: JanusCtx, source: Row, opposing: Row | null, targetStatus: boolean): boolean {
  opposing = opposingOrNull(opposing);
  if (!creatureTypeGateAllows(ctx, source, opposing)) return false;
  if (!targetStatus || !['if_target_has_status_effect_active', 'if_target_has_status_effect_not_active'].some(key => ctx.read.has(source, key))) return true;
  return opposing !== null && targetStatusGatesMet(ctx, source, opposing, 'skill_roll_arr:roll-type-sources');
}
